import { useQuery } from "@tanstack/react-query";
import type { BusinessOption } from "@/business/business";
import { useApi } from "@/core/api";
import type { IUser } from "@/core/auth/auth";
import type { ITenant } from "@/tenant/tenant";

export type ImpersonationUserOption = Pick<
  IUser,
  "id" | "email" | "role" | "parentable_id" | "parentable_type"
> & {
  first_name: string | null;
  last_name: string | null;
};

export const impersonationOptionsKey = ["impersonation", "options"] as const;

export const useImpersonationBusinessOptions = (enabled: boolean) => {
  const api = useApi();
  return useQuery({
    queryKey: [...impersonationOptionsKey, "businesses"],
    queryFn: ({ signal }): Promise<BusinessOption[]> =>
      api.get("/businesses/options", { signal }).then((res) => res.data),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};

export const useImpersonationTenantOptions = (enabled: boolean) => {
  const api = useApi();
  return useQuery({
    queryKey: [...impersonationOptionsKey, "tenants"],
    queryFn: ({ signal }): Promise<ITenant[]> =>
      api.get("/tenants", { signal }).then((res) => res.data),
    enabled,
    retry: false,
  });
};

export const useImpersonationUserOptions = (
  parentType: "business" | "tenant",
  parentId: string,
  enabled: boolean,
) => {
  const api = useApi();
  return useQuery({
    queryKey: [...impersonationOptionsKey, "users", parentType, parentId],
    queryFn: ({ signal }): Promise<ImpersonationUserOption[]> =>
      api
        .get("/users/options", {
          params: { [`${parentType}_id`]: parentId },
          signal,
        })
        .then((res) => res.data),
    enabled: enabled && !!parentId,
    retry: false,
  });
};
